/**
 * Local Heap debugging — logs every heap.track call and keeps a history on
 * window.__heapDebugEvents. Enabled in development by default (AppConfig.HEAP_DEBUG).
 *
 * Optional: set HEAP_ID=2079501096 in .env to also send events to the staging
 * Heap project (see head scripts). The debug wrapper still logs those calls.
 */

const WRAP_POLL_MS = 500;
const WRAP_POLL_MAX_MS = 30_000;

function shouldInstallHeapDebug() {
  return Boolean(window.AppConfig?.HEAP_DEBUG);
}

function recordHeapEvent(eventName, properties) {
  const entry = {
    eventName,
    properties: properties && typeof properties === "object" ? properties : {},
    at: new Date().toISOString(),
  };

  window.__heapDebugEvents = window.__heapDebugEvents || [];
  window.__heapDebugEvents.push(entry);

  // eslint-disable-next-line no-console
  console.groupCollapsed(
    `%c[Heap debug]%c ${eventName}`,
    "color:#e85d04;font-weight:700",
    "color:inherit"
  );
  // eslint-disable-next-line no-console
  console.log(entry.properties);
  // eslint-disable-next-line no-console
  console.log(
    `Captured ${window.__heapDebugEvents.length} event(s). Call window.dumpHeapEvents()`
  );
  // eslint-disable-next-line no-console
  console.groupEnd();

  return entry;
}

function isDebugWrappedTrack(trackFn) {
  return Boolean(trackFn?.__scanDebugTrack);
}

function wrapHeapTrack(heap) {
  if (!heap || isDebugWrappedTrack(heap.track)) {
    return;
  }

  const originalTrack =
    typeof heap.track === "function" ? heap.track.bind(heap) : null;

  const wrappedTrack = (eventName, properties) => {
    recordHeapEvent(eventName, properties);
    if (originalTrack) {
      return originalTrack(eventName, properties);
    }
    return undefined;
  };
  wrappedTrack.__scanDebugTrack = true;
  heap.track = wrappedTrack;
}

function installHeapStub() {
  window.heap = {
    load() {},
    addUserProperties() {},
    addEventProperties() {},
    clearEventProperties() {},
    identify() {},
    resetIdentity() {},
  };
  wrapHeapTrack(window.heap);
}

export function installHeapDebug() {
  if (!shouldInstallHeapDebug() || window.__heapDebugInstalled) {
    return;
  }

  window.__heapDebugInstalled = true;
  window.__heapDebugEvents = window.__heapDebugEvents || [];

  if (window.heap) {
    wrapHeapTrack(window.heap);
  } else {
    installHeapStub();
  }

  // Real Heap may replace heap.track after async load — re-wrap briefly.
  const startedAt = Date.now();
  const pollId = window.setInterval(() => {
    if (window.heap && !isDebugWrappedTrack(window.heap.track)) {
      wrapHeapTrack(window.heap);
    }
    if (Date.now() - startedAt >= WRAP_POLL_MAX_MS) {
      window.clearInterval(pollId);
    }
  }, WRAP_POLL_MS);

  window.dumpHeapEvents = () => {
    const events = window.__heapDebugEvents || [];
    // eslint-disable-next-line no-console
    console.table(
      events.map((entry) => ({
        eventName: entry.eventName,
        at: entry.at,
        properties: JSON.stringify(entry.properties),
      }))
    );
    return events;
  };

  // eslint-disable-next-line no-console
  console.info(
    "%c[Heap debug]%c Enabled. Events log in the console; call %cwindow.dumpHeapEvents()%c for a table.%s",
    "color:#e85d04;font-weight:700",
    "color:inherit",
    "font-family:monospace;font-weight:600",
    "color:inherit",
    window.AppConfig?.HEAP_ID
      ? ` Also forwarding to Heap project ${window.AppConfig.HEAP_ID}.`
      : " Set HEAP_ID=2079501096 in .env to also send to the staging Heap project."
  );
}
